import React, {
  useState, useEffect, Fragment, useMemo,
} from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Table, Drawer, Dropdown, Tag, Button, Tooltip, Popover,
  Empty,
} from 'antd';
import {
  LoadingOutlined, FilterOutlined, ControlOutlined, CopyOutlined, PushpinTwoTone,
} from '@ant-design/icons';
import Helpers from '@Apis/helpers';
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import closeIcon from '@Images/icons/icon-close-blue.png';
import DailyRunRateActions from '@Actions/inventory/dailyRunRateActions';
import AddToCartActions from '@Actions/addToCart/addToCartActions';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import Restricted from '@Components/Restricted';
import DailyRunRateForm from '../DailyRunRateForm';
import DailyRunRateCards from '../DailyRunRateCard';
import DailyRunRateView from '../DailyRunRateView';

import PRZText from '../../../../Common/UI/PRZText';
import useCheckPortrait from '../../../../../hooks/useCheckPortrait';
import './style.scss';
import { toISTDate, entityNameEnum, filterTypeEnums } from '@Apis/constants';
import UserSavedFilterViewSelector from '../../../../Common/UserSavedFilterViewSelector';


const DailyRunRateList = ({
  user, getDailyRunRates, dailyRunRates, getDailyRunRatesLoading, history, deleteDailyRunRateLoading, pinDailyRunRateLoading, getCartV2, cartDataV2, pinDailyRunRate,
}) => {
  const [selectedTenant, setSelectedTenant] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isUserReady, setIsUserReady] = useState(false);
  const [showCreateDailyRunRateModal, setShowCreateDailyRunRateModal] = useState(false);
  const [showDailyRunRateDrawer, setShowDailyRunRateDrawer] = useState(false);
  const [call, setCall] = useState(false);
  const [callCard, setCallCard] = useState(false);
  const [showUpdateDailyRunRateModal, setShowUpdateDailyRunRateModal] = useState(false);
  const [selectedDRR, setSelectedDRR] = useState({});
  const [stockTypeFilter, setStockTypeFilter] = useState(null);
  // states related to saved filters
  const [currentDefaultView, setCurrentDefaultView] = useState({});
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState([]);
  const [selectedFilterView, setSelectedFilterView] = useState({});
  const [isUserReadyInitial, setIsUserReadyInitial] = useState(false);
  const [isUpdateFilterSate, setIsUpdateFilterSate] = useState(true);
  const [savedFilterViews, setSavedFilterViews] = useState([]);
  const [savedFilterViewsLoading, setSavedFilterViewsLoading] = useState(false);

  const {
    isMobile, isPortrait, isMobileAndPortrait, isMobileOrPortrait,
  } = useCheckPortrait();

  const pushParamsToUrl = (currentTenant, page, limit, drr, showDrr, selectedFilterView) => {
    let url = '?tab=/daily-run-rates';
    if (page) url += `&page=${page}`;
    if (limit) url += `&limit=${limit}`;
    if (currentTenant) url += `&tenant=${currentTenant}`;
    if (Object.keys(drr).length) url += `&drr=${drr?.drr_id}`;
    if (showDrr) url += `&showDrr=${showDrr}`;
    if (selectedFilterView?.filter_id) url += `&filter_id=${selectedFilterView?.filter_id}`;

    history.push(url);
  };
  // common 
  function getFilterValueByKey({ filtersArray, key, valueOnly = true }) {
    const selectedFilter = filtersArray.find((item) => item.filter_key === key) || null;
    return valueOnly ? selectedFilter?.filter_value : selectedFilter;
  }

  function getAppliedFiltersFromURL() {
    const appliedFiltersFromURL = [
      {
        filter_key: 'limit',
        filter_value: Number(Helpers.getValueFromUrl('limit') || limit),
        filter_type: filterTypeEnums.NUMBER,
      },
      {
        filter_key: 'tenant',
        filter_value: Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => Number(item)) : [],
        filter_type: filterTypeEnums.ARRAY,
      },
    ];
    return appliedFiltersFromURL;
  }

  const initialFilterValues = useMemo(() => [
    {
      filter_key: 'limit',
      filter_value: limit,
      filter_type: filterTypeEnums.NUMBER,
    },
    {
      filter_key: 'tenant',
      filter_value: [user?.tenant_info?.tenant_id],
      filter_type: filterTypeEnums.ARRAY,
    },
  ], []);
  const systemDefaultUserView = useMemo(() => ({
    filter_id: 'system_default',
    filter_name: 'System Default View',
    entity_name: entityNameEnum.STOCK_LEVEL_TRACKING,
    filters: initialFilterValues,
    tenant_id: user?.tenant_info?.tenant_id,
    org_id: user?.user_org_id,
    user_id: user?.user_id,
    is_default: false,
    is_system_default: true,
  }), []);

  useEffect(() => {
    setSelectedFilterView(systemDefaultUserView);
  }, []);
  useEffect(() => {
    if (savedFilterViews && isUserReadyInitial) {
      const defaultUserView = savedFilterViews.find((item) => item.is_default);

      if (defaultUserView) {
        setCurrentDefaultView(defaultUserView);
      } else {
        setCurrentDefaultView(systemDefaultUserView);
      }

      const appliedFiltersFromURL = getAppliedFiltersFromURL();

      const selectedFilterViewFromURL = Helpers.getValueFromUrl('filter_id');
      let currentSelectedView;
      if (selectedFilterViewFromURL) {
        currentSelectedView = savedFilterViews.find((item) => String(item.filter_id) === String(selectedFilterViewFromURL));
        if (!currentSelectedView) {
          currentSelectedView = systemDefaultUserView;
        }
      } else {
        currentSelectedView = defaultUserView || systemDefaultUserView;
      }

      const filterValues = (isFromURL) => {
        const currentSelectedViewFilters = currentSelectedView.filters;

        const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: isFromURL ? appliedFiltersFromURL : currentSelectedViewFilters, key: filterKey }) }), {});

        return filterValue;
      };

      const tempFilterValues = filterValues(selectedFilterViewFromURL);
      setSelectedFilterView(currentSelectedView);
      if (isUpdateFilterSate) {
        // common state
        setCurrentlyAppliedFilters(appliedFiltersFromURL);
        // temp state
        setCurrentPage(tempFilterValues.page || 1);
        setLimit(tempFilterValues.limit || limit);
        setSelectedTenant(tempFilterValues.tenant);
      }

      if (!isUserReady) setIsUserReady(true);
      if (!isUpdateFilterSate) setIsUpdateFilterSate(true);
    }
  }, [savedFilterViews, isUserReadyInitial]);

  useEffect(() => {
    // if (user?.tenant_info) {
    //   setSelectedTenant(Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (parseInt(item))) : [user?.tenant_info?.tenant_id]);
    //   setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
    //   setLimit(Helpers.getValueFromUrl('limit') || limit);
    //   setIsUserReady(true);
    //   getCartV2({ tenantId: user?.tenant_info?.tenant_id });
    // }

    if (Helpers.getValueFromUrl('drr')) {
      setSelectedDRR({ tenant_id: (Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (parseInt(item))) : [user?.tenant_info?.tenant_id])?.[0], drr_id: Number(Helpers.getValueFromUrl('drr')), list_name: Helpers.getValueFromUrl('list_name') });
      setShowDailyRunRateDrawer(Boolean(Helpers.getValueFromUrl('showDrr')) || showDailyRunRateDrawer);
    }

    return () => {
      setIsUserReady(false);
    };
  }, [call, callCard]);

  useEffect(() => {
    if (isUserReady) {
      pushParamsToUrl(selectedTenant, currentPage, limit, selectedDRR, showDailyRunRateDrawer, selectedFilterView);
      setCurrentlyAppliedFilters(getAppliedFiltersFromURL());
      const payload = {
        tenantId: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.SALES_ORDER, Helpers.permissionTypes.READ)?.join(','),
        page: Helpers.getValueFromUrl('page'),
        limit: Helpers.getValueFromUrl('limit'),
      };
      getDailyRunRates(payload);
    }
    return () => {
    };
  }, [selectedTenant, currentPage, limit]);

  const skuListIds = useMemo(() => {
    if (!dailyRunRates) {
      return [];
    }

    return dailyRunRates?.data?.daily_run_rates.reduce((acc, curr) => {
      if (!acc.includes(curr.sku_list_id)) {
        return [...acc, curr.sku_list_id];
      }
      return acc;
    }, []);
  }, [dailyRunRates]);

  const getColumns = () => {
    const columns = [
      {
        title: '',
        render: (record) => {
          const outwardTenantStocksUnits = record?.outward_tenant_skus;
          const inStock = !outwardTenantStocksUnits || !outwardTenantStocksUnits?.length
            ? 0
            : outwardTenantStocksUnits?.reduce((acc, curr) => (curr?.status === 'In Stock' ? acc + 1 : acc), 0);
          const runningLow = !outwardTenantStocksUnits || !outwardTenantStocksUnits?.length
            ? 0
            : outwardTenantStocksUnits?.reduce((acc, curr) => (curr?.status === 'Running Low' ? acc + 1 : acc), 0);
          const outOfStock = !outwardTenantStocksUnits || !outwardTenantStocksUnits?.length
            ? 0
            : outwardTenantStocksUnits?.reduce((acc, curr) => (curr?.status === 'Out of Stock' ? acc + 1 : acc), 0);
          return (
            <div className="mobile-list__card">
              <div className="mobile-list__card_body">
                <div className="mobile-list__card_body-left">
                  <div className="mobile-list__card_item-name_wrapper">
                    <PRZText
                      text={`${record?.sku_list_info?.list_name}`}
                      className="mobile-list__card_item-name"
                    />
                  </div>
                  <PRZText
                    text={`${record?.tenant_info?.tenant_name}`}
                    className="mobile-list__card_item-details"
                  />

                  {!outwardTenantStocksUnits || !outwardTenantStocksUnits?.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} imageStyle={{ height: '25px' }} />
                  ) : (
                    <Fragment>
                      <div className="drr_card_body_stock">
                        <PRZText
                          text="In Stock"
                          className="drr_card_body_stock_count "
                        />
                        <PRZText
                          text={inStock}
                          className="drr_card_body_stock_count green"
                        />
                      </div>
                      <div className="drr_card_body_stock">
                        <PRZText
                          text="Running Low"
                          className="drr_card_body_stock_count"
                        />
                        <PRZText
                          text={runningLow}
                          className="drr_card_body_stock_count yellow"
                        />
                      </div>
                      <div className="drr_card_body_stock">
                        <PRZText
                          text="Out of Stock"
                          className="drr_card_body_stock_count"
                        />
                        <PRZText
                          text={outOfStock}
                          className="drr_card_body_stock_count red"
                        />
                      </div>
                    </Fragment>

                  )}

                </div>
                <div className="mobile-list__card_body-right">
                  <Button
                    type="ghost"
                    ghost
                    onClick={(e) => {
                      e.stopPropagation();
                      const payload = {
                        drr_id: record?.drr_id,
                        is_pinned: !record?.is_pinned,
                      };
                      pinDailyRunRate(payload, () => {
                        setCallCard(!callCard);
                      });
                    }}
                    icon={record?.is_pinned ? (
                      <PushpinTwoTone
                        twoToneColor="#52c41a"
                        className="drr_card_header_icon"
                      />
                    ) : (
                      <PushpinTwoTone
                        twoToneColor="#ff0000"
                        className="drr_card_header_icon"
                      />
                    )}
                  />
                </div>
              </div>

            </div>
          );
        },
        // responsive: ['xs'],
        hidden: !isMobile,
      },
      {
        title: '',
        render: (record) => (
          <React.Fragment>
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (record?.outward_tenant_skus?.length) {
                  setShowDailyRunRateDrawer(true);
                  setSelectedDRR(record);
                }
              }}
            >
              <DailyRunRateCards
                drrItem={record}
                drrId={record?.drr_id}
                setSelectedDRR={(record) => setSelectedDRR(record)}
                setShowUpdateDailyRunRateModal={setShowUpdateDailyRunRateModal}
                cardTitle={record?.sku_list_info?.list_name}
                outwardTenantStocksUnits={record?.outward_tenant_skus}
                isPinned={record?.is_pinned}
                isLoading={getDailyRunRatesLoading}
                callback={
                  () => {
                    setCallCard(!callCard);
                  }
                }
                setStockTypeFilter={(val) => {
                  setStockTypeFilter(val);
                  setShowDailyRunRateDrawer(true);
                }}
              />
            </div>
          </React.Fragment>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
      },
    ];

    return columns.filter((item) => !item.hidden);
  };

  return (
    <Fragment>
      <div className="drr__wrapper">
        {!user?.tenant_info?.global_config?.sub_modules?.daily_run_rate?.is_active && (
          <Restricted
            style={{
              top: '105px',
            }}
            isOrgActive
            moduleName="Stock Level Tracking"
          />
        )}

        <div className="drr__inner__wrapper">
          <div className="drr__header-wrapper">
            <div className="flex-align-c" style={{ gap: '10px' }}>
              <UserSavedFilterViewSelector
                selectedFilterView={selectedFilterView}
                onChangeTab={(currentSelectedView) => {
                  setSelectedFilterView(() => {
                    const currentSelectedViewFilters = currentSelectedView.filters;

                    const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: currentSelectedViewFilters, key: filterKey }) }), {});

                    const tempFilterValues = filterValue;
                    // common state
                    setCurrentlyAppliedFilters(currentSelectedViewFilters);
                    // temp states
                    setCurrentPage(tempFilterValues.page || 1);
                    setLimit(tempFilterValues.limit || limit);
                    setSelectedTenant(tempFilterValues.tenant);
                    return currentSelectedView;
                  });
                }}
                currentlyAppliedFilters={currentlyAppliedFilters}
                entityName={entityNameEnum.STOCK_LEVEL_TRACKING}
                setSavedFilterViews={(value) => setSavedFilterViews(value)}
                setSavedFilterViewsLoading={(value) => setSavedFilterViewsLoading(value)}
                setIsUpdateFilterSate={(value) => setIsUpdateFilterSate(value)}
                callback={() => {
                  if (!isUserReadyInitial) setIsUserReadyInitial(true);
                }}
                systemDefaultUserView={systemDefaultUserView}
              />
              <div className="header__tags">
                {selectedTenant && selectedTenant?.map((item, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => {
                      setCurrentPage(1);
                      setSelectedTenant(selectedTenant.filter((tId) => tId !== item));
                    }}
                    color="#2d7df7"
                  >
                    Business Unit -
                    &nbsp;
                    <span>
                      <b>{user?.user_tenants?.find((tenant) => tenant?.tenant_id == item)?.tenant_name?.toUpperCase()}</b>
                    </span>
                  </Tag>
                ))}
              </div>
            </div>
            <div className="add-stock-tracker__button hide__in-mobile">
              <Button
                type="primary"
                onClick={() => {
                  setShowCreateDailyRunRateModal(true);
                  // getDocCFV2Success(null);
                }}
              >
                + New Stock Tracker
              </Button>
            </div>
          </div>
          <div className="drr__table__wrapper">

            <div className="generic-table-container drr__table">
              <Table
                title={() => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="">
                      {`${dailyRunRates?.data?.daily_run_rates?.length ?? 0} Stock Tracker(s)`}
                    </div>
                    <div className="filters__buttons">
                      <Dropdown
                        placement="bottom"
                        trigger={['click']}
                        overlay={(
                          <div className="header__filter__container">
                            <div className="header__filter__container__title">
                              Apply Filters
                            </div>
                            <div className="filter__container filter__container-section">
                              <div className="filter__title"> Select Business Unit </div>
                              <div className="sales-orders__header__section-status">
                                <TenantSelector
                                  selectedTenant={selectedTenant}
                                  showSearch
                                  showAll={false}
                                  noDropdownAlign
                                  isMultiple
                                  onChange={(value) => {
                                    setCurrentPage(1);
                                    setSelectedTenant(value);
                                  }}
                                  placeholder="Select Business Unit"
                                  inputClassName="orgInputContainer"
                                  hideTitle
                                  includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.SALES_ORDER, Helpers.permissionTypes.READ)}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      >
                        <div className="action-buttons" style={{ marginLeft: '10px' }}>
                          <div className="action-button">
                            <H3Text text="Filters" className="action-button-text" />
                            <FilterOutlined />
                          </div>
                        </div>
                      </Dropdown>
                    </div>
                  </div>
                )}
                showHeader={false}
                dataSource={dailyRunRates?.data?.daily_run_rates || []}
                columns={getColumns()}
                size="small"
                pagination={
                  {
                    defaultCurrent: currentPage,
                    current: currentPage,
                    total: dailyRunRates?.success ? dailyRunRates?.data?.count : 0,
                    pageSize: limit,
                    showTotal: (tot, range) => `${range[0]}-${range[1]} of ${tot} items`,
                    onChange: (page, pageSize) => {
                      setCurrentPage(page);
                      setLimit(pageSize);
                    },
                    position: ['bottomLeft'],
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100],
                  }
                }
                loading={getDailyRunRatesLoading || deleteDailyRunRateLoading || pinDailyRunRateLoading}
              />
            </div>
          </div>
        </div>

      </div>

      <Drawer
        open={showCreateDailyRunRateModal}
        width="600px"
        mask
        onClose={(e) => {
          e.stopPropagation();
          setShowCreateDailyRunRateModal(false);
        }}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '550px' }}>
            <H3Text text="Create New Stock Level Tracker" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowCreateDailyRunRateModal(false)} />
          </div>
        </div>
        <DailyRunRateForm
          skuListIds={skuListIds}
          callback={
            () => {
              setShowCreateDailyRunRateModal(false);
              setCall(!call);
            }
          }
        />
      </Drawer>

      <Drawer
        open={showDailyRunRateDrawer}
        width="925px"
        mask
        onClose={(e) => {
          e.stopPropagation();
          setShowDailyRunRateDrawer(false);
          setStockTypeFilter(null);
        }}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '875px' }}>
            <H3Text text={`Stock tracker for product group - ${selectedDRR?.sku_list_info?.list_name || selectedDRR?.list_name}`} className="custom-drawer__title" />
            <H3Image
              src={closeIcon}
              className="custom-drawer__close-icon"
              onClick={() => {
                setShowDailyRunRateDrawer(false);
              }}
            />
          </div>
        </div>
        <DailyRunRateView
          selectedDRR={selectedDRR}
          cartSkus={cartDataV2?.result?.products?.map((item) => item?.product_sku_id ?? [])}
          setStockTypeFilter={(val) => setStockTypeFilter(val)}
          stockTypeFilter={stockTypeFilter}
          callback={
            () => {
              setShowDailyRunRateDrawer(false);
              setCall(!call);
            }
          }
        />
      </Drawer>

      <Drawer
        open={showUpdateDailyRunRateModal}
        width="600px"
        mask
        onClose={() => setShowUpdateDailyRunRateModal(false)}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '550px' }}>
            <H3Text text="Update Stock Level Tracker" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowUpdateDailyRunRateModal(false)} />
          </div>
        </div>
        <DailyRunRateForm
          isUpdating
          selectedDRR={selectedDRR}
          callback={
            () => {
              setShowUpdateDailyRunRateModal(false);
              setCall(!call);
            }
          }
        />
      </Drawer>
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, DailyRunRateReducers, AddToCartReducers }) => ({
  user: UserReducers.user,
  getDailyRunRatesLoading: DailyRunRateReducers.getDailyRunRatesLoading,
  dailyRunRates: DailyRunRateReducers.dailyRunRates,
  deleteDailyRunRateLoading: DailyRunRateReducers.deleteDailyRunRateLoading,
  pinDailyRunRateLoading: DailyRunRateReducers.pinDailyRunRateLoading,
  cartDataV2: AddToCartReducers.cartDataV2,
});

const mapDispatchToProps = (dispatch) => ({
  getDailyRunRates: (payload, callback) => dispatch(DailyRunRateActions.getDailyRunRates(payload, callback)),
  getCartV2: (payload, callback) => dispatch(AddToCartActions.getCartV2(payload, callback)),
  pinDailyRunRate: (payload, callback) => dispatch(DailyRunRateActions.pinDailyRunRate(payload, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DailyRunRateList));
